import React, { useState, useEffect } from 'react';
import ClientTable from '../../components/ClientTable/ClientTable';
import ClientForm from '../../components/ClientForm/ClientForm';
import { getClients, addClient, updateClient, addTransaction, deleteClient, getTransactions } from '../../services/clientService';
import styles from './Clients.module.css';

export const getNextCycleDate = (startDateStr, currentDueStr) => {
    if (!startDateStr || !currentDueStr) return new Date().toISOString();
    const start = new Date(startDateStr);
    let d1 = start.getDate();
    if (d1 > 28) d1 = 28;

    let d2 = d1 + 15;
    if (d1 > 15) d2 = d1 - 15;
    else if (d1 === 15) d2 = 30;

    let nextDue = new Date(currentDueStr);
    nextDue.setDate(nextDue.getDate() + 1);
    
    for (let i = 0; i < 40; i++) {
        const testDay = nextDue.getDate();
        if (testDay === d1 || testDay === d2) {
            return nextDue.toISOString();
        }
        nextDue.setDate(nextDue.getDate() + 1);
    }
    return nextDue.toISOString();
};

const Clients = ({ filter = 'all' }) => {
  const [showForm, setShowForm] = useState(false);
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingClient, setEditingClient] = useState(null);

  const [paymentModal, setPaymentModal] = useState({ isOpen: false, client: null, amount: '', date: '' });

  const [allTransactions, setAllTransactions] = useState([]);
  const [summaryModal, setSummaryModal] = useState({ isOpen: false, client: null, transactions: [] });

  useEffect(() => {
    const fetchData = async () => {
      const [data, txs] = await Promise.all([getClients(), getTransactions()]);
      setClients(data);
      setAllTransactions(txs || []);
      setIsLoading(false);
    };
    fetchData();
  }, []);

  const handleAddClient = async (clientData) => {
    setIsSubmitting(true);
    try {
      if (editingClient) {
        const total = parseFloat(clientData.totalAmount || 0);
        const received = parseFloat(clientData.receivedAmount || 0);
        const remaining = total - received;

        const updatedData = {
          ...clientData,
          remainingAmount: remaining
        };

        await updateClient(editingClient.id, updatedData);
        setClients(clients.map(c => c.id === editingClient.id ? { ...c, ...updatedData } : c));
        setEditingClient(null);
      } else {
        const total = parseFloat(clientData.totalAmount || 0);
        const received = parseFloat(clientData.receivedAmount || 0);
        const remaining = total - received;
        
        const nextDueStr = getNextCycleDate(clientData.startDate, clientData.startDate);

        const newClient = await addClient({
          ...clientData,
          remainingAmount: remaining,
          nextDueDate: nextDueStr,
          lastPaymentDate: received > 0 ? new Date(clientData.paymentDate || new Date()).toISOString() : null
        });

        if (received > 0) {
          const newTx = await addTransaction({
            clientId: newClient.id,
            clientName: newClient.clientName || newClient.name || 'Unknown',
            amount: received,
            type: 'Advance Payment',
            date: new Date(clientData.paymentDate || new Date()).toISOString()
          });
          setAllTransactions([newTx, ...allTransactions]);
        }
        
        setClients([newClient, ...clients]);
        setShowForm(false);
      }
    } catch (error) {
      console.error("Failed to save client", error);
      alert("Failed to save client. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setShowForm(false);
  };

  const handleCancelEdit = () => {
    setEditingClient(null);
  };

  const openPaymentModal = (client) => {
    setPaymentModal({
      isOpen: true,
      client,
      amount: '',
      date: new Date().toISOString().split('T')[0]
    });
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    const { client, amount, date } = paymentModal;
    const parsedAmount = parseFloat(amount);
    
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      alert("Invalid amount entered.");
      return;
    }

    try {
      const currentReceived = parseFloat(client.receivedAmount || 0);
      const total = parseFloat(client.totalAmount || 0);

      const updatedReceived = currentReceived + parsedAmount;
      const updatedRemaining = total - updatedReceived;
      
      let fallbackDue = new Date(client.lastPaymentDate || client.startDate);
      fallbackDue.setDate(fallbackDue.getDate() + 15);
      const currentDueStr = client.nextDueDate || fallbackDue.toISOString();
      
      const nextDueStr = getNextCycleDate(client.startDate, currentDueStr);

      const paymentDateIso = new Date(date).toISOString();

      await updateClient(client.id, {
        receivedAmount: updatedReceived,
        remainingAmount: updatedRemaining,
        nextDueDate: nextDueStr,
        lastPaymentDate: paymentDateIso
      });

      const newTx = await addTransaction({
        clientId: client.id,
        clientName: client.name || client.clientName || 'Unknown',
        amount: parsedAmount,
        type: 'Payment',
        date: paymentDateIso
      });
      setAllTransactions([newTx, ...allTransactions]);

      setClients(clients.map(c => 
        c.id === client.id 
          ? { ...c, receivedAmount: updatedReceived, remainingAmount: updatedRemaining, nextDueDate: nextDueStr }
          : c
      ));

      setPaymentModal({ isOpen: false, client: null, amount: '', date: '' });
    } catch (error) {
       console.error("Error updating payment", error);
       alert("Failed to update payment.");
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (window.confirm("Are you sure you want to completely delete this client? This cannot be undone.")) {
      try {
        await deleteClient(clientId);
        setClients(clients.filter(c => c.id !== clientId));
      } catch (err) {
        console.error(err);
        alert("Failed to delete client.");
      }
    }
  };

  const handleToggleStatus = async (client) => {
    const newStatus = client.status === 'inactive' ? 'active' : 'inactive';
    if (window.confirm(`Are you sure you want to mark this client as ${newStatus}?`)) {
      try {
        await updateClient(client.id, { status: newStatus });
        setClients(clients.map(c => c.id === client.id ? { ...c, status: newStatus } : c));
      } catch (err) {
        console.error(err);
        alert(`Failed to update status to ${newStatus}.`);
      }
    }
  };

  const handleViewSummary = (client) => {
    const clientTxs = allTransactions.filter(tx => tx.clientId === client.id).sort((a,b) => new Date(b.date) - new Date(a.date));
    setSummaryModal({ isOpen: true, client, transactions: clientTxs });
  };

  const todayStr = new Date().toDateString();

  const filteredClients = clients.filter(c => {
    const searchMatch = c.clientName?.toLowerCase().includes(searchQuery.toLowerCase()) || 
      c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.channelName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.channel?.toLowerCase().includes(searchQuery.toLowerCase());
      
    if (filter === 'today') {
      const createdStr = new Date(c.createdAt || c.startDate).toDateString();
      return searchMatch && createdStr === todayStr;
    }
    
    return searchMatch;
  });

  return (
    <div className={styles.clientsContainer}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.pageTitle}>{filter === 'today' ? "Today's Onboarded Clients" : "Clients Management"}</h1>
          <p className={styles.pageSubtitle}>
            {filter === 'today' 
              ? "View clients you've acquired today." 
              : "View and manage all your client relationships."}
          </p>
        </div>
        <button 
          className={styles.addBtn}
          onClick={() => {
            setEditingClient(null);
            setShowForm(!showForm);
          }}
          disabled={isSubmitting}
        >
          {showForm ? 'Cancel' : '+ Add New Client'}
        </button>
      </div>

      {isLoading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading clients...</div>
      ) : showForm || editingClient ? (
        <div className={styles.formWrapper}>
          <ClientForm 
            onSubmit={handleAddClient} 
            initialData={editingClient} 
            onCancel={editingClient ? handleCancelEdit : () => setShowForm(false)} 
          />
        </div>
      ) : (
        <>
          <div className={styles.searchContainer}>
            <input 
              type="text" 
              placeholder="Search clients by name..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>
          <div className={styles.tableWrapper}>
            <ClientTable 
              clients={filteredClients} 
              onAddPayment={openPaymentModal} 
              onEditClient={handleEditClient}
              onDeleteClient={handleDeleteClient}
              onToggleStatus={handleToggleStatus}
              onViewSummary={handleViewSummary}
            />
          </div>
        </>
      )}

      {/* Payment Modal */}
      {paymentModal.isOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000}}>
          <div style={{ backgroundColor: '#1e293b', padding: '30px', borderRadius: '12px', width: '90%', maxWidth: '400px', border: '1px solid #334155', boxShadow: '0 10px 25px rgba(0,0,0,0.5)' }}>
            <h2 style={{ margin: '0 0 5px 0', fontSize: '20px', color: 'white' }}>Record Payment</h2>
            <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '20px' }}>
              For {paymentModal.client?.name || paymentModal.client?.clientName}
            </p>
            
            <form onSubmit={submitPayment}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', color: '#cbd5e1', marginBottom: '5px', fontSize: '14px' }}>Amount Received (₹)</label>
                <input 
                  type="number" 
                  autoFocus
                  required
                  value={paymentModal.amount}
                  onChange={e => setPaymentModal({...paymentModal, amount: e.target.value})}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #334155', backgroundColor: '#0f172a', color: 'white', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: '25px' }}>
                <label style={{ display: 'block', color: '#cbd5e1', marginBottom: '5px', fontSize: '14px' }}>Date Received</label>
                <input 
                  type="date" 
                  required
                  value={paymentModal.date}
                  onChange={e => setPaymentModal({...paymentModal, date: e.target.value})}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #334155', backgroundColor: '#0f172a', color: 'white', boxSizing: 'border-box' }}
                />
              </div>
              
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button 
                  type="button" 
                  onClick={() => setPaymentModal({ isOpen: false, client: null, amount: '', date: '' })}
                  style={{ backgroundColor: 'transparent', color: '#94a3b8', border: '1px solid #475569', padding: '10px 16px', borderRadius: '6px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  style={{ backgroundColor: '#10b981', color: 'white', border: 'none', padding: '10px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  Save Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Summary Modal */}
      {summaryModal.isOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000}}>
          <div style={{ backgroundColor: '#1e293b', padding: '30px', borderRadius: '12px', width: '90%', maxWidth: '500px', border: '1px solid #334155', boxShadow: '0 10px 25px rgba(0,0,0,0.5)', maxHeight: '80vh', overflowY: 'auto' }}>
            <h2 style={{ margin: '0 0 5px 0', fontSize: '20px', color: 'white' }}>Payment Summary</h2>
            <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '20px' }}>
              Transaction history for {summaryModal.client?.name || summaryModal.client?.clientName}
            </p>
            
            {summaryModal.transactions.length === 0 ? (
              <p style={{ color: '#64748b', textAlign: 'center', padding: '20px 0' }}>No payments recorded yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                {summaryModal.transactions.map(tx => (
                  <div key={tx.id} style={{ display: 'flex', justifyContent: 'space-between', backgroundColor: '#0f172a', padding: '12px', borderRadius: '8px', border: '1px solid #334155' }}>
                    <div>
                      <span style={{ color: 'white', display: 'block', fontWeight: 'bold' }}>{tx.type}</span>
                      <span style={{ color: '#64748b', fontSize: '12px' }}>{new Date(tx.date).toDateString()}</span>
                    </div>
                    <div style={{ color: '#10b981', fontWeight: 'bold', fontSize: '16px' }}>
                      + ₹{parseFloat(tx.amount).toLocaleString('en-IN')}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #334155', paddingTop: '15px' }}>
              <button 
                type="button" 
                onClick={() => setSummaryModal({ isOpen: false, client: null, transactions: [] })}
                style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
